import { Component, input, output } from '@angular/core';
import { SelectionType } from '../../types/selection.type';
import { selectRowsBetween, selectRows } from '../../utils/selection';
import { Keys } from '../../utils/keys';

export interface Model {
  type: string;
  event: MouseEvent | KeyboardEvent;
  row: unknown;
  rowElement: HTMLElement;
  cellElement: HTMLElement;
  cellIndex: number;
}

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'datatable-selection',
  template: ' <ng-content /> '
})
export class DataTableSelectionComponent {
  readonly rows = input<Array<unknown>>(undefined);
  readonly selected = input<Array<unknown>>(undefined);
  readonly selectEnabled = input<boolean>(undefined);
  readonly selectionType = input<SelectionType>(undefined);
  readonly rowIdentity = input<(row: unknown) => unknown>(undefined);
  readonly selectCheck = input<((row: unknown) => boolean) | undefined>(undefined);

  readonly activate = output<unknown>();
  readonly select = output<{ selected: Array<unknown> }>();

  prevIndex: number;

  selectRow(event: KeyboardEvent | MouseEvent, index: number, row: unknown): void {
    if (!this.selectEnabled()) {return;}

    const chkbox = this.selectionType() === SelectionType.checkbox;
    const multi = this.selectionType() === SelectionType.multi;
    const multiClick = this.selectionType() === SelectionType.multiClick;
    let selected: Array<unknown>;

    if (multi || chkbox || multiClick) {
      if (event.shiftKey) {
        selected = selectRowsBetween([], this.rows(), index, this.prevIndex, this.getRowSelectedIdx.bind(this));
      } else if (event.ctrlKey || event.metaKey || multiClick || chkbox) {
        selected = selectRows([...this.selected()], row, this.getRowSelectedIdx.bind(this));
      } else {
        selected = selectRows([], row, this.getRowSelectedIdx.bind(this));
      }
    } else {
      selected = selectRows([], row, this.getRowSelectedIdx.bind(this));
    }

    const selectCheck = this.selectCheck();
    if (typeof selectCheck === 'function') {
      selected = selected.filter(selectCheck.bind(this));
    }

    const selectedValue = this.selected();
    this.selected().splice(0, selectedValue.length);
    selectedValue.push(...selected);

    this.prevIndex = index;

    this.select.emit({
      selected
    });
  }

  onActivate(model: Model, index: number): void {
    const { type, event, row }: Model = model;
    const chkbox = this.selectionType() === SelectionType.checkbox;
    const select = (!chkbox && (type === 'click' || type === 'dblclick')) || (chkbox && type === 'checkbox');

    if (select) {
      this.selectRow(event, index, row);
    } else if (type === 'keydown') {
      if ((event as KeyboardEvent).keyCode === Keys.return) {
        this.selectRow(event, index, row);
      } else {
        this.onKeyboardFocus(model);
      }
    }
    this.activate.emit(model);
  }

  onKeyboardFocus(model: Model): void {
    const { keyCode }: KeyboardEvent = model.event as KeyboardEvent;
    const shouldFocus = keyCode === Keys.up || keyCode === Keys.down || keyCode === Keys.right || keyCode === Keys.left;

    if (shouldFocus) {
      const isCellSelection = this.selectionType() === SelectionType.cell;

      if (!model.cellElement || !isCellSelection) {
        this.focusRow(model.rowElement, keyCode);
      } else if (isCellSelection) {
        this.focusCell(model.cellElement, model.rowElement, keyCode, model.cellIndex);
      }
    }
  }

  focusRow(rowElement: HTMLElement, keyCode: number): void {
    const nextRowElement = this.getPrevNextRow(rowElement, keyCode);
    if (nextRowElement) {nextRowElement.focus();}
  }

  getPrevNextRow(rowElement: HTMLElement, keyCode: number): HTMLElement | undefined {
    const parentElement = rowElement.parentElement;

    if (parentElement) {
      let focusElement: HTMLElement;
      if (keyCode === Keys.up) {
        focusElement = parentElement.previousElementSibling as HTMLElement;
      } else if (keyCode === Keys.down) {
        focusElement = parentElement.nextElementSibling as HTMLElement;
      }

      if (focusElement && focusElement.children.length) {
        return focusElement.children[0] as HTMLElement;
      }
    }
  }

  focusCell(cellElement: HTMLElement, rowElement: HTMLElement, keyCode: number, cellIndex: number): void {
    let nextCellElement: HTMLElement;

    if (keyCode === Keys.left) {
      nextCellElement = cellElement.previousElementSibling as HTMLElement;
    } else if (keyCode === Keys.right) {
      nextCellElement = cellElement.nextElementSibling as HTMLElement;
    } else if (keyCode === Keys.up || keyCode === Keys.down) {
      const nextRowElement = this.getPrevNextRow(rowElement, keyCode);
      if (nextRowElement) {
        const children = nextRowElement.getElementsByClassName('datatable-body-cell');
        if (children.length) {nextCellElement = children[cellIndex] as HTMLElement;}
      }
    }

    if (nextCellElement) {nextCellElement.focus();}
  }

  getRowSelected(row: unknown): boolean {
    return this.getRowSelectedIdx(row, this.selected()) > -1;
  }

  getRowSelectedIdx(row: unknown, selected: Array<unknown>): number {
    if (!selected || !selected.length) {return -1;}

    const rowId = this.rowIdentity()(row);
    return selected.findIndex((r: unknown) => {
      const id = this.rowIdentity()(r);
      return id === rowId;
    });
  }
}
